import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

    // Estado del modal de toma de chat con etiqueta
    const [takeChatModalOpen, setTakeChatModalOpen] = useState(false);
    const [labels, setLabels] = useState([]);
    const [selectedLabelId, setSelectedLabelId] = useState("");
    const [loadingLabels, setLoadingLabels] = useState(false);

    // Estado del modal de Seguimiento
    const [followUpModalOpen, setFollowUpModalOpen] = useState(false);

    // Cargar etiquetas cuando se abre el modal
    const handleOpenTakeChatModal = async () => {
        setTakeChatModalOpen(true);
        setLoadingLabels(true);
        try {
            const { data } = await api.get("/labels");
            // Filtramos solo las manuales (no del sistema)
            const manualLabels = data.filter(l => !l.isSystem);
            setLabels(manualLabels);
        } catch (err) {
            toast.error("Error cargando etiquetas");
        }
        setLoadingLabels(false);
    };

    const handleTakeChatWithLabel = async () => {
        if (!selectedLabelId) {
            toast.warning("Debes seleccionar una etiqueta para tomar el chat.");
            return;
        }
        setLoading(true);
        try {
            // Asignar etiqueta primero
            const currentLabels = ticket.contact.labels ? ticket.contact.labels.map(t => t.id) : [];
            if (!currentLabels.includes(selectedLabelId)) {
                await api.put(`/contacts/${ticket.contact.id}`, {
                    labelIds: [...currentLabels, selectedLabelId]
                });
            }

            // Luego tomar el chat
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id || null,
            });

            setTakeChatModalOpen(false);
            setLoading(false);
            history.push(`/tickets/${ticket.id}`);
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

    const handleReactivateAi = async () => {
        setLoading(true);
        try {
            await api.post(`/tickets/${ticket.id}/reactivate-ai`);
            toast.success("La IA ha enviado un mensaje para reactivar la conversación.");
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePassToFollowUp = async () => {
        setLoading(true);
        try {
            await api.post(`/tickets/${ticket.id}/pass-to-followup`);
            toast.success("Contacto enviado a seguimiento. El bot retomará cuando vuelva a escribir.");
            setFollowUpModalOpen(false);
            history.push("/tickets");
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const isFollowUpChat = ticket?.contact?.labels?.some(l => l.name === "Seguimiento");

	return (
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					style={{ color: "#1a1a2e" }}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && ticket.userId !== null && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
						style={{ color: "#1a1a2e" }}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						style={{ display: "none" }}
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu} style={{ display: "none", color: "#1a1a2e" }}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "open" && ticket.userId === null && (
                <>
                    {!isFollowUpChat && (
                        <>
                            <ButtonWithSpinner
                                loading={loading}
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={handleReactivateAi}
                                style={{ marginRight: 8, borderColor: "#00D4AA", color: "#00D4AA" }}
                            >
                                Activar conversación
                            </ButtonWithSpinner>

                            <ButtonWithSpinner
                                loading={loading}
                                size="small"
                                variant="outlined"
                                onClick={() => setFollowUpModalOpen(true)}
                                style={{ borderColor: "#7d3c98", color: "#7d3c98", marginLeft: 4, marginRight: 8 }}
                            >
                                Seguimiento
                            </ButtonWithSpinner>
                        </>
                    )}

                    <ButtonWithSpinner
                        loading={loading}
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={handleOpenTakeChatModal}
                    >
                        Tomar Chat
                    </ButtonWithSpinner>
                </>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={handleOpenTakeChatModal}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}

            {/* Modal Obligatorio de Etiqueta */}
            <Dialog open={takeChatModalOpen} onClose={() => setTakeChatModalOpen(false)}>
                <DialogTitle>Toma de Chat</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" style={{ marginBottom: 15 }}>
                        Para tomar este chat y ocultar al Agente IA, debes clasificar al cliente con una etiqueta manual obligatoria.
                    </Typography>
                    <Autocomplete
                        options={labels}
                        getOptionLabel={(option) => option.name}
                        onChange={(e, newValue) => {
                            setSelectedLabelId(newValue ? newValue.id : "");
                        }}
                        disabled={loadingLabels}
                        renderOption={(option) => (
                            <div style={{ color: option.color, fontWeight: "bold" }}>
                                {option.name}
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Buscar y asignar Etiqueta" 
                                variant="outlined" 
                                fullWidth 
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTakeChatModalOpen(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button onClick={handleTakeChatWithLabel} color="primary" variant="contained" disabled={loading || !selectedLabelId}>
                        Confirmar y Tomar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Modal de Seguimiento */}
            <Dialog open={followUpModalOpen} onClose={() => setFollowUpModalOpen(false)}>
                <DialogTitle>Pasar a Seguimiento</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1">
                        ¿Desea agregar a <strong>{ticket.contact.name}</strong> a la pestaña de seguimientos?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
                        El bot dejará de responder temporalmente hasta que el cliente vuelva a escribir.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFollowUpModalOpen(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button onClick={handlePassToFollowUp} color="primary" variant="contained" disabled={loading}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
		</div>
	);
};

export default TicketActionButtons;
